import {
  ActiveInstanceResponse,
  cancelWorkflow,
  hasInstanceRunning,
  launchWorkflow,
  LaunchWorkflowResult,
} from "@autorujira/workflow-launcher";
import {
  WorkflowOperationMessagesResult,
  WorkflowOperationResult,
} from "@autorujira/workflow-launcher/dist/utils/workflowLifecycleService";
import { useCallback, useMemo, useState } from "react";
import { useAccounts } from "../services/accounts";
import { dataProviders } from "../services/auto";

interface WorkflowConfig {
  config?: {
    wizardDisplay?: "never" | "always" | "auto";
  };
  workflowName: string;
  appId?: string;
  title?: string;
  parameters?: Record<string, any>;
  executionType?: string;
  cronExpression?: string;
  onlyGenerateMessages?: boolean;
  onComplete?: (result: any) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

interface UseWorkflowLauncherReturn {
  launchWorkflow: (config: WorkflowConfig) => Promise<LaunchWorkflowResult>;
  hasInstanceRunning: (launcherId: string) => Promise<ActiveInstanceResponse>;
  cancelWorkflow: (config: {
    instanceId: string;
    onlyGenerateMessages?: boolean;
    onComplete?: (result: any) => void;
    onError?: (error: Error) => void;
  }) => Promise<WorkflowOperationResult | WorkflowOperationMessagesResult>;
  isLoading: boolean;
  error: Error | null;
  clearError: () => void;
}

export const useWorkflowLauncher = (): UseWorkflowLauncherReturn => {
  const { selected } = useAccounts();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create a single wallet client instance that updates when selected changes
  const walletClient = useMemo(() => {
    if (!selected) {
      return null;
    }
    // Minimal wallet client for message generation
    return {
      getAccounts: async () => [{ address: selected.address }],
    };
  }, [selected]);

  const launchWorkflowHandler = useCallback(
    async (config: WorkflowConfig) => {
      if (!walletClient) {
        throw new Error("No wallet connected");
      }

      setIsLoading(true);
      setError(null);

      try {
        // Generate messages without executing
        const result = await launchWorkflow({
          workflowName: config.workflowName,
          title: config.title,
          parameters: config.parameters ?? {},
          executionType: config.executionType as "OneShot" | "Recurrent",
          cronExpression: config.cronExpression,
          walletClient,
          dataProviders,
          theme: "dark",
          appId: config.appId,
          onlyGenerateMessages: true,
          config: {
            wizardDisplay: "never",
          },
          onComplete: config.onComplete,
          onError: config.onError,
          onClose: config.onClose,
        });

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [walletClient]
  );

  const hasInstanceRunningHandler = useCallback(
    async (launcherId?: string) => {
      if (!walletClient) {
        throw new Error("No wallet connected");
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await hasInstanceRunning(
          walletClient,
          undefined, // workflowId
          launcherId,
          dataProviders
        );
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [walletClient]
  );

  const cancelWorkflowHandler = useCallback(
    async (config: {
      instanceId: string;
      onlyGenerateMessages?: boolean;
      onComplete?: (result: any) => void;
      onError?: (error: Error) => void;
    }) => {
      if (!walletClient) {
        throw new Error("No wallet connected");
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await cancelWorkflow({
          instanceId: config.instanceId,
          walletClient,
          onlyGenerateMessages: config.onlyGenerateMessages,
          onComplete: config.onComplete,
          onError: config.onError,
        });
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [walletClient]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    launchWorkflow: launchWorkflowHandler,
    hasInstanceRunning: hasInstanceRunningHandler,
    cancelWorkflow: cancelWorkflowHandler,
    isLoading,
    error,
    clearError,
  };
};
